import React, { useState, useEffect } from 'react';
import { users, currencies } from '../../services/api';

function UsersList() {
  const [usersList, setUsersList] = useState([]);
  const [currenciesList, setCurrenciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    user_login: '',
    user_password: '',
    user_base_currency: '',
    user_is_admin: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, currenciesRes] = await Promise.all([
        users.getAll(),
        currencies.getAll()
      ]);
      setUsersList(usersRes.data);
      setCurrenciesList(currenciesRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      // Don't send empty password on update
      if (editingId && !dataToSend.user_password) {
        delete dataToSend.user_password;
      }

      if (editingId) {
        await users.update(editingId, dataToSend);
      } else {
        await users.create(dataToSend);
      }
      loadData();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setFormData({
      user_login: user.user_login,
      user_password: '',
      user_base_currency: user.user_base_currency,
      user_is_admin: !!user.user_is_admin
    });
    setEditingId(user.user_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await users.delete(id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      user_login: '',
      user_password: '',
      user_base_currency: '',
      user_is_admin: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2>Users</h2>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Users</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add User
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>{editingId ? 'Edit User' : 'Create User'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Login *</label>
              <input
                type="text"
                value={formData.user_login}
                onChange={(e) => setFormData({ ...formData, user_login: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Password {editingId ? '(leave empty to keep current)' : '*'}</label>
              <input
                type="password"
                value={formData.user_password}
                onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                required={!editingId}
              />
            </div>

            <div className="form-group">
              <label>Base Currency</label>
              <select
                value={formData.user_base_currency || ''}
                onChange={(e) => setFormData({ ...formData, user_base_currency: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Auto-select first available</option>
                {currenciesList.map(curr => (
                  <option key={curr.curr_id} value={curr.curr_id}>
                    {curr.curr_symbol_code}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.user_is_admin}
                  onChange={(e) => setFormData({ ...formData, user_is_admin: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                Administrator
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <table className="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Login</th>
            <th>Base Currency</th>
            <th>Admin</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {usersList.map((user) => (
            <tr key={user.user_id}>
              <td>{user.user_id}</td>
              <td>{user.user_login}</td>
              <td>{user.curr_symbol_code || '-'}</td>
              <td>
                {user.user_is_admin ? (
                  <span className="badge badge-success">Yes</span>
                ) : (
                  <span className="badge badge-secondary">No</span>
                )}
              </td>
              <td>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleEdit(user)}
                  style={{ marginRight: '5px' }}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(user.user_id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UsersList;
